import { useMsalToken } from "@/hooks/use-msal-token";
import { API_ENDPOINTS, TAG_BUILDER } from "./abstractions/api-types";
import type {
  ShiftDate,
  GetShiftDatesRequest,
  ShiftDateActionRequest,
  ConfirmShiftDatePaymentRequest
} from "@/domain/shift-dates";
import type { VerificationDecisionRequest } from "@/domain/admin";
import { useAuthenticatedApi } from "./abstractions/use-authenticated-api";
import type {
  GetProfessionalStatsRequest,
  GetProfessionalStatsResponse,
  GetProfessionalRequest,
  GetProfessionalResponse,
  UpdateProfessionalRequest,
  UpdateProfessionalResponse,
  GetProfessionalAvailabilityRequest,
  GetProfessionalAvailabilityResponse,
  AddProfessionalAvailabilityRequest,
  RemoveProfessionalAvailabilityRequest,
  RegisterProfessionalRequest,
  RegisterProfessionalResponse,
  GetProfessionalRegistrationLookupsResponse,
  GetProfessionalRegistrationLookupsRequest,
  GetProfessionalDocumentsResponse,
  GetProfessionalDocumentsRequest,
  UpdateProfessionalDocumentResponse,
  UpdateProfessionalDocumentRequest,
  UploadProfessionalDocumentResponse,
  UploadProfessionalDocumentRequest,
  DeleteProfessionalDocumentResponse,
  DeleteProfessionalDocumentRequest,
  DownloadProfessionalDocumentResponse,
  DownloadProfessionalDocumentRequest,
  GetProfessionalCertificatesResponse,
  GetProfessionalCertificatesRequest,
  DeleteProfessionalCertificateResponse,
  DeleteProfessionalCertificateRequest,
  UploadProfessionalCertificateResponse,
  UploadProfessionalCertificateRequest,
  UpdateProfessionalCertificateResponse,
  UpdateProfessionalCertificateRequest,
  GetProfessionalShiftsRequest,
  GetProfessionalShiftsResponseItem,
  GetAppliedShiftsRequest,
  GetAppliedShiftsResponseItem,
  GetProfessionalUpcomingShiftsRequest,
  GetProfessionalUpcomingShiftsResponseItem,
  GetProfessionalShiftRequest,
  GetProfessionalShiftDetailResponse,
  GetProfessionalShiftOpportunityRequest,
  GetProfessionalShiftOpportunityResponse,
  ApplyToShiftApiRequest,
  ApplyToShiftResponse,
  GetProfessionalQuickStatsRequest,
  GetProfessionalQuickStatsResponse,
  GetProfessionalCalendarStatsRequest,
  GetProfessionalCalendarStatsResponse,
  GetProfessionalPaymentsOverviewRequest,
  GetProfessionalPaymentsOverviewResponse,
  GetProfessionalTierProgressRequest,
  GetProfessionalTierProgressResponse,
  GetProfessionalReferralsRequest,
  GetProfessionalReferralsResponse,
  WithdrawShiftApplicationRequest,
  WithdrawShiftApplicationResponse,
  AcceptShiftNominationRequest,
  AcceptShiftNominationResponse,
  RatePracticeApiRequest,
  RatePracticeResponse,
  GetProfessionalRatingsRequest,
  GetProfessionalRatingsResponse,
  ProfessionalSettingsResponse,
  UpdateProfessionalSettingsRequest,
  SearchShiftsRequest,
  SearchShiftsResponse,
  GetRecommendedShiftsRequest,
  GetRecommendedShiftsResponse,
  RecordProfessionalRecommendationEventRequest,
  GetFavouritePracticesRequest,
  GetFavouritePracticesResponse,
  AddPracticeToFavouritesRequest,
  AddPracticeToFavouritesResponse,
  RemovePracticeFromFavouritesRequest,
  RemovePracticeFromFavouritesResponse,
  CreateProfessionalSpecialtyRequest,
  CreateProfessionalSkillRequest
} from "@/domain/professionals";
import type {
  GetNotificationsRequest,
  GetNotificationsResponse
} from "@/domain/core";
import type {
  RaiseDisputeRequest,
  GetDisputesRequest,
  GetDisputesResponse
} from "@/domain/payments";
import type { PaginatedResponse } from "@/domain/core";
import {
  buildHttpDelete,
  buildHttpGet,
  buildHttpGetForDownload,
  buildHttpPost,
  buildHttpPut
} from "@/utilities/api-utils";
import { config } from "@/config/environment";
import {
  DASHBOARD_QUERY_OPTIONS,
  DEFAULT_QUERY_OPTIONS,
  NOTIFICATION_QUERY_OPTIONS
} from "./abstractions/api-config";
import { DEFAULT_PAGE_SIZE } from "@/constants";

// Authenticated Professionals API hook
export function useAuthenticatedProfessionalsApi() {
  const authenticatedApi = useAuthenticatedApi();
  const { addAuthHeader } = useMsalToken();

  const professionalsApi = authenticatedApi.injectEndpoints({
    overrideExisting: false,
    // Endpoints
    endpoints: (builder) => ({
      // Get professional statistics
      getProfessionalStats: builder.query<
        GetProfessionalStatsResponse,
        GetProfessionalStatsRequest
      >({
        query: async ({ professionalId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.STATS(professionalId)
          ),
        providesTags: [TAG_BUILDER.ADMIN.REPORTS()],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      // Get all professionals

      // Get specific professional
      getProfessional: builder.query<
        GetProfessionalResponse,
        GetProfessionalRequest
      >({
        query: async ({ id }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.GET(id)
          ),
        providesTags: (_, __, { id }) => [TAG_BUILDER.PROFESSIONAL.ENTITY(id)],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Staff verification decision. Mirrors verifyPractice: refreshes the detail
      // query. The admin list is a mutation and is re-run explicitly by
      // ProfessionalsManager.
      verifyProfessional: builder.mutation<
        string,
        { professionalId: string } & VerificationDecisionRequest
      >({
        query: async ({ professionalId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.VERIFY(professionalId),
            body
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId),
          TAG_BUILDER.PROFESSIONAL.LIST(),
          TAG_BUILDER.ADMIN.REPORTS()
        ]
      }),

      // Update professional
      updateProfessional: builder.mutation<
        UpdateProfessionalResponse,
        UpdateProfessionalRequest
      >({
        query: async (request) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.UPDATE(request.id),
            request
          ),
        invalidatesTags: (_, __, { id }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(id),
          TAG_BUILDER.PROFESSIONAL.LIST(),
          TAG_BUILDER.ADMIN.REPORTS()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional availability
      getProfessionalAvailability: builder.query<
        GetProfessionalAvailabilityResponse,
        GetProfessionalAvailabilityRequest
      >({
        query: async (professionalId) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.AVAILABILITY(professionalId)
          ),
        providesTags: (_, __, professionalId) =>
          TAG_BUILDER.PROFESSIONAL.AVAILABILITY(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Add professional availability entry
      addProfessionalAvailability: builder.mutation<
        string,
        AddProfessionalAvailabilityRequest
      >({
        query: async ({ professionalId, availabilityId, dayOfWeek }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.AVAILABILITIES(professionalId),
            { availabilityId, dayOfWeek }
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.AVAILABILITY(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Remove professional availability entry
      removeProfessionalAvailability: builder.mutation<
        string,
        RemoveProfessionalAvailabilityRequest
      >({
        query: async ({ professionalId, availabilityId, dayOfWeek }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.AVAILABILITY_ENTRY(professionalId, availabilityId, dayOfWeek)
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.AVAILABILITY(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Add professional specialty
      addProfessionalSpecialty: builder.mutation<
        string,
        CreateProfessionalSpecialtyRequest
      >({
        query: async ({ professionalId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SPECIALTIES(professionalId),
            body
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Remove professional specialty
      removeProfessionalSpecialty: builder.mutation<
        string,
        { professionalId: string; specialtyId: string }
      >({
        query: async ({ professionalId, specialtyId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SPECIALTY(professionalId, specialtyId)
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Add professional skill
      addProfessionalSkill: builder.mutation<
        string,
        CreateProfessionalSkillRequest
      >({
        query: async ({ professionalId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SKILLS(professionalId),
            body
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Remove professional skill
      removeProfessionalSkill: builder.mutation<
        string,
        { professionalId: string; skillId: string }
      >({
        query: async ({ professionalId, skillId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SKILL(professionalId, skillId)
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),


      // Register professional
      registerProfessional: builder.mutation<
        RegisterProfessionalResponse,
        RegisterProfessionalRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.REGISTER,
            body
          ),
        invalidatesTags: (_, __, { userId }) => [
          TAG_BUILDER.USER.ENTITY(userId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional registration lookups
      getProfessionalRegistrationLookups: builder.query<
        GetProfessionalRegistrationLookupsResponse,
        GetProfessionalRegistrationLookupsRequest
      >({
        query: async () =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.LOOKUPS
          ),
        providesTags: () => [TAG_BUILDER.PROFESSIONAL.LOOKUPS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional documents
      getProfessionalDocuments: builder.query<
        GetProfessionalDocumentsResponse,
        GetProfessionalDocumentsRequest
      >({
        query: async ({ professionalId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.DOCUMENTS(professionalId)
          ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.DOCUMENTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Upload single document i.e. dental license file
      uploadProfessionalDocument: builder.mutation<
        UploadProfessionalDocumentResponse,
        UploadProfessionalDocumentRequest
      >({
        query: async ({ professionalId, formData }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.UPLOAD_DOCUMENT(professionalId),
            formData
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.DOCUMENTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update a professional document based on document ID
      updateProfessionalDocument: builder.mutation<
        UpdateProfessionalDocumentResponse,
        UpdateProfessionalDocumentRequest
      >({
        query: async ({ professionalId, documentId, description }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.DOCUMENT(professionalId, documentId),
            { description }
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.DOCUMENTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Delete a professional document base on document ID
      deleteProfessionalDocument: builder.mutation<
        DeleteProfessionalDocumentResponse,
        DeleteProfessionalDocumentRequest
      >({
        query: async ({ professionalId, documentId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.DOCUMENT(professionalId, documentId)
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.DOCUMENTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Download a professional document
      downloadProfessionalDocument: builder.query<
        DownloadProfessionalDocumentResponse,
        DownloadProfessionalDocumentRequest
      >({
        query: async ({ professionalId, documentId }) =>
          await buildHttpGetForDownload(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.DOWNLOAD_DOCUMENT(professionalId, documentId)
          ),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional certificates
      getProfessionalCertificates: builder.query<
        GetProfessionalCertificatesResponse,
        GetProfessionalCertificatesRequest
      >({
        query: async ({ professionalId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CERTIFICATES(professionalId)
          ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.CERTIFICATES(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // delete professional certificate on the basis of certificate id
      deleteProfessionalCertificate: builder.mutation<
        DeleteProfessionalCertificateResponse,
        DeleteProfessionalCertificateRequest
      >({
        query: async ({ professionalId, certificateId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CERTIFICATE(
              professionalId,
              certificateId
            )
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.CERTIFICATES(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // upload a new certificate
      uploadProfessionalCertificate: builder.mutation<
        UploadProfessionalCertificateResponse,
        UploadProfessionalCertificateRequest
      >({
        query: async ({ professionalId, formData }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CERTIFICATES(professionalId),
            formData
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.CERTIFICATES(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // update an existing certificate by id
      updateProfessionalCertificate: builder.mutation<
        UpdateProfessionalCertificateResponse,
        UpdateProfessionalCertificateRequest
      >({
        query: async ({ professionalId, certificateId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CERTIFICATE(
              professionalId,
              certificateId
            ),
            body
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.CERTIFICATES(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // ================================================================
      // Favourites
      // ================================================================

      // Get favourite practices for filter dropdown
      getFavouritePractices: builder.query<
        GetFavouritePracticesResponse,
        GetFavouritePracticesRequest
      >({
        query: async (professionalId) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.FAVOURITE_PRACTICES(professionalId)
          ),
        providesTags: (_, __, professionalId) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Add practice to favourites
      addPracticeToFavourites: builder.mutation<
        AddPracticeToFavouritesResponse,
        AddPracticeToFavouritesRequest
      >({
        query: async ({ professionalId, practiceId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.ADD_PRACTICE_TO_FAVOURITES(professionalId, practiceId),
            {}
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Remove practice from favourites
      removePracticeFromFavourites: builder.mutation<
        RemovePracticeFromFavouritesResponse,
        RemovePracticeFromFavouritesRequest
      >({
        query: async ({ professionalId, practiceId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.REMOVE_PRACTICE_FROM_FAVOURITES(professionalId, practiceId)
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // ================================================================
      // Professional Shift Endpoints
      // ================================================================

      // Get professional shifts (paginated, with status/date filtering)
      getProfessionalShifts: builder.query<
        PaginatedResponse<GetProfessionalShiftsResponseItem>,
        GetProfessionalShiftsRequest
      >({
        query: async ({
          professionalId,
          ...queryParams
        }) => await buildHttpGet(
          config,
          addAuthHeader,
          API_ENDPOINTS.PROFESSIONALS.SHIFTS(professionalId),
          queryParams
        ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get applied shifts
      getAppliedShifts: builder.query<
        PaginatedResponse<GetAppliedShiftsResponseItem>,
        GetAppliedShiftsRequest
      >({
        query: async ({
          professionalId,
          ...queryParams
        }) => await buildHttpGet(
          config,
          addAuthHeader,
          API_ENDPOINTS.PROFESSIONALS.APPLIED_SHIFTS(professionalId),
          queryParams
        ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get upcoming shifts
      getProfessionalUpcomingShifts: builder.query<
        PaginatedResponse<GetProfessionalUpcomingShiftsResponseItem>,
        GetProfessionalUpcomingShiftsRequest
      >({
        query: async ({
          professionalId,
          ...queryParams
        }) => await buildHttpGet(
          config,
          addAuthHeader,
          API_ENDPOINTS.PROFESSIONALS.UPCOMING_SHIFTS(professionalId),
          queryParams
        ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get single professional shift detail
      getProfessionalShift: builder.query<
        GetProfessionalShiftDetailResponse,
        GetProfessionalShiftRequest
      >({
        query: async ({ professionalId, shiftId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SHIFT(professionalId, shiftId)
          ),
        providesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      /** Contract: Tempii `GetProfessionalShiftOpportunityResponse` — sync `GetProfessionalShiftOpportunityResponse` in domain when DTO changes (Phase E). */
      getProfessionalShiftOpportunity: builder.query<
        GetProfessionalShiftOpportunityResponse,
        GetProfessionalShiftOpportunityRequest
      >({
        query: async ({ professionalId, shiftId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SHIFT_OPPORTUNITY(
              professionalId,
              shiftId
            )
          ),
        providesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Apply to shift
      applyToShift: builder.mutation<
        ApplyToShiftResponse,
        ApplyToShiftApiRequest
      >({
        query: async ({ professionalId, shiftId, hourlyRate, notes }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.APPLY_TO_SHIFT(professionalId, shiftId),
            { hourlyRate, notes }
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // --- Dated shift instances ------------------------------------------
      // The only place a client learns a shiftApplicationPostId, which every
      // check-in and completion call is keyed by.

      getProfessionalShiftDates: builder.query<ShiftDate[], GetShiftDatesRequest>({
        query: async ({ ownerId, shiftId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SHIFT_DATES(ownerId, shiftId)
          ),
        providesTags: (_, __, { ownerId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(ownerId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      checkInToShiftDate: builder.mutation<string, ShiftDateActionRequest>({
        query: async ({ ownerId, shiftDateId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CHECK_IN_SHIFT_DATE(ownerId, shiftDateId),
            {}
          ),
        invalidatesTags: (_, __, { ownerId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(ownerId)
      }),

      checkOutOfShiftDate: builder.mutation<string, ShiftDateActionRequest>({
        query: async ({ ownerId, shiftDateId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CHECK_OUT_SHIFT_DATE(ownerId, shiftDateId),
            {}
          ),
        invalidatesTags: (_, __, { ownerId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(ownerId)
      }),

      claimShiftDateComplete: builder.mutation<string, ShiftDateActionRequest>({
        query: async ({ ownerId, shiftDateId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.COMPLETE_SHIFT_DATE(ownerId, shiftDateId),
            {}
          ),
        invalidatesTags: (_, __, { ownerId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(ownerId)
      }),

      confirmShiftDatePayment: builder.mutation<string, ConfirmShiftDatePaymentRequest>({
        query: async ({ professionalId, paymentId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CONFIRM_PAYMENT(professionalId, paymentId),
            {}
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId)
      }),

      // Get professional quick stats
      getProfessionalQuickStats: builder.query<
        GetProfessionalQuickStatsResponse,
        GetProfessionalQuickStatsRequest
      >({
        query: async ({ professionalId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.QUICK_STATS(professionalId)
          ),
        providesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      // Get professional calendar stats
      getProfessionalCalendarStats: builder.query<
        GetProfessionalCalendarStatsResponse,
        GetProfessionalCalendarStatsRequest
      >({
        query: async ({ professionalId, startDate, endDate }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.CALENDAR_STATS(professionalId),
            { startDate, endDate }
          ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SCHEDULE(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      getProfessionalPaymentsOverview: builder.query<
        GetProfessionalPaymentsOverviewResponse,
        GetProfessionalPaymentsOverviewRequest
      >({
        query: async ({
          professionalId,
          page = 1,
          pageSize = DEFAULT_PAGE_SIZE,
          sortBy,
          sortOrder = "desc",
          searchTerm
        }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.PAYMENTS_OVERVIEW(professionalId),
            { page, pageSize, sortBy, sortOrder, searchTerm }
          ),
        providesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId),
          TAG_BUILDER.PAYMENT.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Withdraw shift application
      withdrawShiftApplication: builder.mutation<
        WithdrawShiftApplicationResponse,
        WithdrawShiftApplicationRequest
      >({
        query: async ({ professionalId, shiftId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.WITHDRAW_SHIFT(
              professionalId,
              shiftId
            ),
            {}
          ),
        invalidatesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Professional accepts practice nomination (enables practice Accept on nominated applications)
      acceptShiftNomination: builder.mutation<
        AcceptShiftNominationResponse,
        AcceptShiftNominationRequest
      >({
        query: async ({ professionalId, shiftId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.ACCEPT_SHIFT_NOMINATION(
              professionalId,
              shiftId
            ),
            {}
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          ...TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional tier progress
      getProfessionalTierProgress: builder.query<
        GetProfessionalTierProgressResponse,
        GetProfessionalTierProgressRequest
      >({
        query: async ({ professionalId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.TIER_PROGRESS(professionalId)
          ),
        providesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      getProfessionalReferrals: builder.query<
        GetProfessionalReferralsResponse,
        GetProfessionalReferralsRequest
      >({
        query: async ({ professionalId, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.REFERRALS(professionalId),
            { page, pageSize }
          ),
        providesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      // Rate a practice
      ratePractice: builder.mutation<
        RatePracticeResponse,
        RatePracticeApiRequest
      >({
        query: async ({ professionalId, practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.RATE_PRACTICE(professionalId, practiceId),
            body
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          ...TAG_BUILDER.PROFESSIONAL.REVIEWS(professionalId),
          TAG_BUILDER.REVIEW.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional ratings
      getProfessionalRatings: builder.query<
        GetProfessionalRatingsResponse,
        GetProfessionalRatingsRequest
      >({
        query: async ({ professionalId, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.RATINGS(professionalId),
            { page, pageSize }
          ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.REVIEWS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Search shifts
      searchShifts: builder.query<
        SearchShiftsResponse,
        SearchShiftsRequest
      >({
        query: async ({ professionalId, ...queryParams }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SEARCH_SHIFTS(professionalId),
            queryParams
          ),
        providesTags: [TAG_BUILDER.SHIFT.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      getRecommendedShifts: builder.query<
        GetRecommendedShiftsResponse,
        GetRecommendedShiftsRequest
      >({
        query: async ({ professionalId, ...queryParams }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.RECOMMENDED_SHIFTS(professionalId),
            queryParams
          ),
        providesTags: [TAG_BUILDER.SHIFT.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      recordRecommendationEvent: builder.mutation<
        void,
        RecordProfessionalRecommendationEventRequest
      >({
        query: async ({ professionalId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.RECOMMENDATION_EVENTS(professionalId),
            { ...body, professionalId }
          ),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional settings
      getProfessionalSettings: builder.query<
        ProfessionalSettingsResponse,
        { professionalId: string }
      >({
        query: async ({ professionalId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SETTINGS(professionalId)
          ),
        providesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update professional settings
      updateProfessionalSettings: builder.mutation<
        string,
        UpdateProfessionalSettingsRequest
      >({
        query: async ({ professionalId, ...body }) =>
          await buildHttpPut(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.SETTINGS(professionalId),
            body
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Raise a dispute
      raiseProfessionalDispute: builder.mutation<
        string,
        RaiseDisputeRequest & { professionalId: string }
      >({
        query: async ({ professionalId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.RAISE_DISPUTE(professionalId),
            body
          ),
        invalidatesTags: (_, __, { professionalId }) => [
          ...TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
          TAG_BUILDER.PAYMENT.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional disputes
      getProfessionalDisputes: builder.query<
        GetDisputesResponse,
        GetDisputesRequest & { professionalId: string }
      >({
        query: async ({ professionalId, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.DISPUTES(professionalId),
            { page, pageSize }
          ),
        providesTags: (_, __, { professionalId }) =>
          TAG_BUILDER.PROFESSIONAL.SHIFTS(professionalId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get professional notifications
      getProfessionalNotifications: builder.query<
        GetNotificationsResponse,
        GetNotificationsRequest
      >({
        query: async ({
          entityId,
          page = 1,
          pageSize = DEFAULT_PAGE_SIZE,
          isRead,
          searchTerm,
          sortBy,
          sortOrder
        }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PROFESSIONALS.NOTIFICATIONS(entityId),
            { page, pageSize, isRead, searchTerm, sortBy, sortOrder }
          ),
        providesTags: (_, __, { entityId }) => [
          TAG_BUILDER.NOTIFICATION.FOR_ENTITY(entityId)
        ],
        ...NOTIFICATION_QUERY_OPTIONS
      })
    })
  });

  return {
    useGetProfessionalStatsQuery: professionalsApi.useGetProfessionalStatsQuery,
    useGetProfessionalQuery: professionalsApi.useGetProfessionalQuery,
    useUpdateProfessionalMutation:
      professionalsApi.useUpdateProfessionalMutation,
    useGetProfessionalAvailabilityQuery:
      professionalsApi.useGetProfessionalAvailabilityQuery,
    useAddProfessionalAvailabilityMutation:
      professionalsApi.useAddProfessionalAvailabilityMutation,
    useRemoveProfessionalAvailabilityMutation:
      professionalsApi.useRemoveProfessionalAvailabilityMutation,
    useRegisterProfessionalMutation:
      professionalsApi.useRegisterProfessionalMutation,
    useGetProfessionalRegistrationLookupsQuery:
      professionalsApi.useGetProfessionalRegistrationLookupsQuery,
    useGetProfessionalDocumentsQuery:
      professionalsApi.useGetProfessionalDocumentsQuery,
    useUploadProfessionalDocumentMutation:
      professionalsApi.useUploadProfessionalDocumentMutation,
    useUpdateProfessionalDocumentMutation:
      professionalsApi.useUpdateProfessionalDocumentMutation,
    useDeleteProfessionalDocumentMutation:
      professionalsApi.useDeleteProfessionalDocumentMutation,
    useLazyDownloadProfessionalDocumentQuery:
      professionalsApi.useLazyDownloadProfessionalDocumentQuery,
    useGetProfessionalCertificatesQuery:
      professionalsApi.useGetProfessionalCertificatesQuery,
    useDeleteProfessionalCertificateMutation:
      professionalsApi.useDeleteProfessionalCertificateMutation,
    useUploadProfessionalCertificateMutation:
      professionalsApi.useUploadProfessionalCertificateMutation,
    useUpdateProfessionalCertificateMutation:
      professionalsApi.useUpdateProfessionalCertificateMutation,
    // Professional Shift hooks
    useGetProfessionalShiftsQuery:
      professionalsApi.useGetProfessionalShiftsQuery,
    useGetAppliedShiftsQuery: professionalsApi.useGetAppliedShiftsQuery,
    useGetProfessionalUpcomingShiftsQuery:
      professionalsApi.useGetProfessionalUpcomingShiftsQuery,
    useGetProfessionalShiftQuery:
      professionalsApi.useGetProfessionalShiftQuery,
    useGetProfessionalShiftOpportunityQuery:
      professionalsApi.useGetProfessionalShiftOpportunityQuery,
    useVerifyProfessionalMutation: professionalsApi.useVerifyProfessionalMutation,
    useApplyToShiftMutation: professionalsApi.useApplyToShiftMutation,
    useGetProfessionalShiftDatesQuery: professionalsApi.useGetProfessionalShiftDatesQuery,
    useCheckInToShiftDateMutation: professionalsApi.useCheckInToShiftDateMutation,
    useCheckOutOfShiftDateMutation: professionalsApi.useCheckOutOfShiftDateMutation,
    useClaimShiftDateCompleteMutation: professionalsApi.useClaimShiftDateCompleteMutation,
    useConfirmShiftDatePaymentMutation: professionalsApi.useConfirmShiftDatePaymentMutation,
    useGetProfessionalQuickStatsQuery:
      professionalsApi.useGetProfessionalQuickStatsQuery,
    useGetProfessionalCalendarStatsQuery:
      professionalsApi.useGetProfessionalCalendarStatsQuery,
    useGetProfessionalPaymentsOverviewQuery:
      professionalsApi.useGetProfessionalPaymentsOverviewQuery,
    useGetProfessionalTierProgressQuery:
      professionalsApi.useGetProfessionalTierProgressQuery,
    useGetProfessionalReferralsQuery:
      professionalsApi.useGetProfessionalReferralsQuery,
    useWithdrawShiftApplicationMutation:
      professionalsApi.useWithdrawShiftApplicationMutation,
    useAcceptShiftNominationMutation:
      professionalsApi.useAcceptShiftNominationMutation,
    useRatePracticeMutation: professionalsApi.useRatePracticeMutation,
    useGetProfessionalRatingsQuery:
      professionalsApi.useGetProfessionalRatingsQuery,
    useGetProfessionalNotificationsQuery:
      professionalsApi.useGetProfessionalNotificationsQuery,
    useRaiseProfessionalDisputeMutation:
      professionalsApi.useRaiseProfessionalDisputeMutation,
    useGetProfessionalDisputesQuery:
      professionalsApi.useGetProfessionalDisputesQuery,
    useGetProfessionalSettingsQuery:
      professionalsApi.useGetProfessionalSettingsQuery,
    useUpdateProfessionalSettingsMutation:
      professionalsApi.useUpdateProfessionalSettingsMutation,
    useSearchShiftsQuery: professionalsApi.useSearchShiftsQuery,
    useGetRecommendedShiftsQuery: professionalsApi.useGetRecommendedShiftsQuery,
    useRecordRecommendationEventMutation:
      professionalsApi.useRecordRecommendationEventMutation,
    // Favourites hooks
    useGetFavouritePracticesQuery: professionalsApi.useGetFavouritePracticesQuery,
    useAddPracticeToFavouritesMutation:
      professionalsApi.useAddPracticeToFavouritesMutation,
    useRemovePracticeFromFavouritesMutation:
      professionalsApi.useRemovePracticeFromFavouritesMutation,
    // Specialty & Skill hooks
    useAddProfessionalSpecialtyMutation:
      professionalsApi.useAddProfessionalSpecialtyMutation,
    useRemoveProfessionalSpecialtyMutation:
      professionalsApi.useRemoveProfessionalSpecialtyMutation,
    useAddProfessionalSkillMutation:
      professionalsApi.useAddProfessionalSkillMutation,
    useRemoveProfessionalSkillMutation:
      professionalsApi.useRemoveProfessionalSkillMutation
  };
}
